import { Message, MessageInput } from '../../types/chat';
import { supabase } from '../supabase';

// UUID generation polyfill for Safari
function generateUUID(): string {
  // Check if crypto.randomUUID is available
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

class ChatService {
  private static instance: ChatService;
  private subscription: ReturnType<typeof supabase.channel> | null = null;
  private messageCallbacks: ((message: Message) => void)[] = [];
  private connectionCallbacks: ((status: boolean) => void)[] = [];
  private clientId = generateUUID();
  private reconnectAttempts = 0;
  private lastMessageTime = 0;
  private maxReconnectAttempts = 5;
  private isConnecting = false;
  private currentSponsor: any = null;
  private currentTemplate: any = null;
  private retryQueue: { input: MessageInput; attempts: number }[] = [];
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly MIN_MESSAGE_INTERVAL = 5000; // 5 seconds minimum between messages
  private readonly WELCOME_MESSAGE_DELAY = 2000; // 2 second delay for welcome message

  private constructor() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  setCurrentSponsor(sponsor: any, template: any) {
    const hadPreviousSponsor = !!this.currentSponsor;
    this.currentSponsor = sponsor;
    this.currentTemplate = template;
    const isNewSponsor = !hadPreviousSponsor && sponsor;

    console.log('Setting current sponsor:', {
      sponsor: sponsor?.sponsor_metadata?.name,
      template: template?.message,
      active: template?.active
    });

    // Only setup if we have valid sponsor data
    if (isNewSponsor && sponsor && template && template.active && sponsor.active) {
      // Send welcome message with slight delay to ensure connection is ready
      setTimeout(() => {
        this.sendSponsorMessage(true); // true indicates welcome message
      }, this.WELCOME_MESSAGE_DELAY);
    }
  }

  private sendSponsorMessage(isWelcome: boolean = false) {
    if (!this.currentSponsor || !this.currentTemplate) return;

    const now = Date.now();
    const endTime = new Date(this.currentSponsor.end_time);

    // Prevent message spam by enforcing minimum interval (skip for welcome message)
    if (!isWelcome && now - this.lastMessageTime < this.MIN_MESSAGE_INTERVAL) {
      return;
    }

    // Stop if tournament is over
    if (now > endTime.getTime()) {
      return;
    }

    // Replace placeholders in template
    const message = this.currentTemplate.message
      .replace(/\[Sponsor Name\]/g, this.currentSponsor.sponsor_metadata.name)
      .replace(/\[Website\]/g, this.currentSponsor.sponsor_metadata.website || '');

    console.log('Sending sponsor message:', {
      content: message,
      type: 'broadcast',
      isWelcome,
      tournamentId: this.currentSponsor.tournament_id
    });

    // Send sponsor message
    this.sendMessage({
      content: message,
      type: 'broadcast',
      tournamentId: this.currentSponsor.tournament_id,
      isAdmin: true,
      metadata: {
        priority: isWelcome ? 'medium' : 'low',
        category: 'sponsor',
        expiresAt: new Date(now + 30 * 60 * 1000).toISOString() // 30 minutes
      }
    }).catch(error => {
      console.error('Failed to send sponsor message:', error);
    });

    this.lastMessageTime = now;
  }

  static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  private handleOnline = () => {
    if (!this.subscription) {
      this.setupSubscription();
      this.processRetryQueue();
    }
  };

  private handleOffline = () => {
    this.disconnect();
  };

  connect() {
    if (!supabase) {
      console.warn('Supabase not configured');
      return false;
    }

    if (this.subscription?.state === 'SUBSCRIBED') {
      // Re-setup sponsor with welcome message if needed
      if (this.currentSponsor && this.currentTemplate) {
        this.setCurrentSponsor(this.currentSponsor, this.currentTemplate);
      }
      return true;
    }

    return this.setupSubscription();
  }

  private setupSubscription(): boolean {
    if (!supabase || this.isConnecting) return false;
    if (this.subscription?.state === 'SUBSCRIBED') return true;

    try {
      this.isConnecting = true;

      // Clean up existing subscription if any
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }

      // Create new subscription
      this.subscription = supabase
        .channel(`messages-${this.clientId}`, {
          config: {
            broadcast: { self: true },
            presence: { key: this.clientId }
          }
        })
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `id=neq.${this.clientId}`
          },
          async (payload) => {
            try {
              const message: Message = {
                id: payload.new.id,
                senderId: payload.new.sender_id,
                senderName: payload.new.sender_name,
                content: payload.new.content,
                type: payload.new.type,
                isAdmin: payload.new.is_admin,
                createdAt: payload.new.created_at,
                tournamentId: payload.new.tournament_id,
                metadata: payload.new.metadata
              };
              this.notifyMessageReceived(message);
            } catch (error) {
              console.error('Error processing message payload:', error);
            }
          }
        )
        .subscribe((status) => {
          this.isConnecting = false;
          const isConnected = status === 'SUBSCRIBED';
          
          console.log('Chat subscription status:', status);
          this.notifyConnectionChange(isConnected);
          
          if (isConnected) {
            this.reconnectAttempts = 0;
          } else if (status === 'CHANNEL_ERROR') {
            this.handleReconnect();
          }
        });

      return true;
    } catch (error) {
      console.error('Supabase channel error:', error);
      this.isConnecting = false;
      this.handleReconnect();
      return false;
    }
  }

  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('Max chat reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    setTimeout(() => {
      if (this.subscription?.state !== 'SUBSCRIBED') {
        this.setupSubscription();
      }
    }, backoffDelay);
  }

  async sendMessage(input: MessageInput): Promise<void> {
    if (!supabase) return;

    // Validate input
    if (!input.content?.trim() || !input.tournamentId) {
      throw new Error('Invalid message input');
    }

    // Add to retry queue if offline
    if (!navigator.onLine) {
      this.retryQueue.push({ input, attempts: 0 });
      throw new Error('Currently offline - message will be sent when connection is restored');
    }

    const clientId = generateUUID();

    // Generate message ID for deduplication
    const message: Message = {
      id: clientId,
      senderId: clientId,
      senderName: input.isAdmin ? 'Admin' : 'Anonymous User',
      content: input.content,
      type: input.type,
      isAdmin: input.isAdmin || false,
      createdAt: new Date().toISOString(),
      tournamentId: input.tournamentId,
      metadata: input.metadata
    };

    try {
      console.log('Sending message:', message);

      // Save to Supabase with upsert to handle potential duplicates
      const { error: dbError } = await supabase
        .from('messages')
        .upsert([{
          id: message.id,
          sender_id: message.senderId,
          sender_name: message.senderName,
          content: message.content,
          type: message.type,
          is_admin: message.isAdmin,
          tournament_id: message.tournamentId,
          metadata: message.metadata
        }], {
          onConflict: 'id',
          ignoreDuplicates: true
        });

      if (dbError) {
        // Handle specific error cases
        if (dbError.code === '23503') { // Foreign key violation
          throw new Error('Tournament not found');
        } else if (dbError.code === '23514') { // Check constraint violation
          throw new Error('Invalid message type');
        } else if (dbError.message?.includes('Failed to fetch')) {
          throw new Error('Connection error - message will be retried');
        } else {
          throw dbError;
        }
      }

      console.log('Message saved successfully');

      // Notify subscribers immediately for responsive UX
      this.notifyMessageReceived(message);

      // Process any queued messages
      this.processRetryQueue();

    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Add to retry queue for connection errors
      if (error instanceof Error &&
          (error.message.includes('Failed to fetch') ||
           error.message.includes('Network Error') ||
           error.message.includes('Connection error'))) {
        this.retryQueue.push({ input, attempts: 0 });
        throw new Error('Message will be sent when connection is restored');
      }
      
      throw error;
    }
  }

  private async validateConnection(): Promise<boolean> {
    if (!supabase) return false;
    
    try {
      const { error } = await supabase
        .from('messages')
        .select('id')
        .limit(1);

      return !error || error.message?.includes('Failed to fetch');
    } catch {
      return false;
    }
  }

  private async processRetryQueue() {
    if (!navigator.onLine || this.retryQueue.length === 0) return;

    const currentQueue = [...this.retryQueue];
    this.retryQueue = [];

    for (const item of currentQueue) {
      try {
        if (item.attempts >= this.MAX_RETRY_ATTEMPTS) {
          console.warn('Max retry attempts reached for message:', item.input);
          continue;
        }

        await this.sendMessage(item.input);
      } catch (error) {
        item.attempts++;
        if (item.attempts < this.MAX_RETRY_ATTEMPTS) {
          this.retryQueue.push(item);
        }
      }
    }
  }

  onMessage(callback: (message: Message) => void) {
    this.messageCallbacks.push(callback);
  }

  onConnectionChange(callback: (status: boolean) => void) {
    this.connectionCallbacks.push(callback);
  }

  private notifyMessageReceived(message: Message) {
    this.messageCallbacks.forEach(callback => callback(message));
  }

  private notifyConnectionChange(status: boolean) {
    this.connectionCallbacks.forEach(callback => callback(status));
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this.isConnecting = false;
    this.notifyConnectionChange(false);
  }

  isConnected(): boolean {
    return this.subscription?.state === 'SUBSCRIBED';
  }

}

export const chatService = ChatService.getInstance();